import { useI18n } from '@ecom/i18n';
import { type UseThemeResult } from '@ecom/ui-kit';
import { Command } from 'cmdk';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { NAV_ITEMS } from './nav';

/** Opens the palette from elsewhere in the shell (the top bar's search field). */
export const COMMAND_PALETTE_OPEN_EVENT = 'souk:command-palette-open';

export function openCommandPalette(): void {
  window.dispatchEvent(new Event(COMMAND_PALETTE_OPEN_EVENT));
}

/**
 * Cmd/Ctrl+K palette — navigation (available screens only) and the theme toggle.
 *
 * `theme` is passed in from AppShell rather than called locally — see the
 * comment on AppShell's own `useTheme()` call for why a second, independent
 * instance here would desync from the topbar's toggle.
 */
export function CommandPalette({ theme: themeState }: { theme: UseThemeResult }): ReactElement {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();
  // `toggleTheme` is called via `themeState.toggleTheme()` rather than
  // destructured — it's a method-shorthand on `UseThemeResult`, and
  // destructuring it would detach it from `themeState` (unbound-method).
  const { theme } = themeState;

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
      if (event.key === 'Escape') setOpen(false);
    }
    // The top bar's search field (Figma « Souk 2.0 ») opens this same palette.
    function handleOpen(): void {
      setOpen(true);
    }
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener(COMMAND_PALETTE_OPEN_EVENT, handleOpen);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener(COMMAND_PALETTE_OPEN_EVENT, handleOpen);
    };
  }, []);

  // Every screen, labelled with the menu entry it folds under (« Inventaire — Produits »).
  const availableItems = NAV_ITEMS;

  return (
    <AnimatePresence>
      {open ? (
        <Command.Dialog
          open={open}
          onOpenChange={setOpen}
          label={t('merchant.commandPalette.label')}
          className="fixed inset-0 z-50"
        >
          <motion.div
            className="fixed inset-0 bg-[rgb(8_10_18_/_0.54)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setOpen(false);
            }}
          />
          <motion.div
            className="fixed inset-x-0 top-24 mx-auto w-[min(560px,92vw)] overflow-hidden rounded-2xl border border-line bg-surface shadow-popover"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
          >
            <Command.Input
              autoFocus
              placeholder={t('merchant.commandPalette.placeholder')}
              className="w-full border-b border-line bg-transparent px-4 py-3.5 text-sm text-ink outline-none placeholder:text-inkFaint"
            />
            <Command.List className="max-h-80 overflow-y-auto p-2">
              <Command.Empty className="px-3 py-6 text-center text-sm text-inkFaint">
                {t('merchant.commandPalette.empty')}
              </Command.Empty>

              <Command.Group
                heading={t('merchant.commandPalette.navigationGroup')}
                className="px-2 py-1 text-xs font-medium text-inkFaint"
              >
                {availableItems.map((item) => (
                  <Command.Item
                    key={item.to}
                    onSelect={() => {
                      void navigate(item.to);
                      setOpen(false);
                    }}
                    className="flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-sm text-ink data-[selected=true]:bg-accentSoft data-[selected=true]:text-accentInk"
                  >
                    <span>{t(item.labelKey)}</span>
                    {item.parentKey !== null ? (
                      <span className="text-xs text-inkFaint">{t(item.parentKey)}</span>
                    ) : null}
                  </Command.Item>
                ))}
              </Command.Group>

              <Command.Group
                heading={t('merchant.commandPalette.preferencesGroup')}
                className="px-2 py-1 text-xs font-medium text-inkFaint"
              >
                <Command.Item
                  onSelect={() => {
                    themeState.toggleTheme();
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm text-ink data-[selected=true]:bg-accentSoft data-[selected=true]:text-accentInk"
                >
                  {theme === 'dark'
                    ? t('merchant.commandPalette.toggleThemeToLight')
                    : t('merchant.commandPalette.toggleThemeToDark')}
                </Command.Item>
              </Command.Group>
            </Command.List>
          </motion.div>
        </Command.Dialog>
      ) : null}
    </AnimatePresence>
  );
}
